import React, { useEffect, useState } from 'react';
import { trackFunnelEvent } from '../analytics';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle,
  Groups,
  RocketLaunch,
  School,
  Work,
} from '@mui/icons-material';

const SKOOL_URL = 'https://www.skool.com/raymond/about';
const LOOM_URL = 'https://www.loom.com/embed/a0e001d7c449435d90adaa0e6db48791';
const FOUNDATION_LOOM_URL = 'https://www.loom.com/embed/afc5bcb3e0e34977ad93ceb17848fcee';

type EmploymentStatus =
  | ''
  | 'employed_swe'
  | 'employed_technical'
  | 'previously_swe_unemployed'
  | 'student'
  | 'not_employed'
  | 'career_switcher';

type ExperienceLevel = '' | 'under_1' | '1_2' | '3_5' | '6_plus';
type ApplicationOutcome = 'idle' | 'qualified' | 'needs_foundation';

const communityBenefits = [
  'The real resume that helped me get big-tech interviews',
  'My LeetCode roadmap for becoming a top 3% problem solver',
  'Weekly support from engineers preparing for better offers',
  'Simple systems for resumes, interviews, and coding consistency',
];

const qualificationSignals = [
  {
    icon: <Work sx={{ fontSize: 34 }} />,
    title: 'Currently employed as a software engineer',
    copy: 'This system is optimized for current software engineers who only have 2 hours a day to study.',
  },
  {
    icon: <RocketLaunch sx={{ fontSize: 34 }} />,
    title: 'At least 1 year of experience',
    copy: 'The community is built for people already in the field who want stronger interviews.',
  },
  {
    icon: <Groups sx={{ fontSize: 34 }} />,
    title: 'Actively preparing for a better role',
    copy: 'You will get roadmaps, accountability, and support from people doing the same work.',
  },
];

const getQualificationReason = (
  employmentStatus: EmploymentStatus,
  experienceLevel: ExperienceLevel
) => {
  if (employmentStatus !== 'employed_swe') {
    return 'not_currently_employed_as_swe';
  }

  if (experienceLevel === 'under_1') {
    return 'under_one_year_experience';
  }

  return 'qualified';
};

const SkoolAdsLandingPage: React.FC = () => {
  const [employmentStatus, setEmploymentStatus] = useState<EmploymentStatus>('');
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>('');
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState<ApplicationOutcome>('idle');

  useEffect(() => {
    document.title = 'Free SWE Interview Community - Raymond Jones';
  }, []);

  const sendQualifiedEvent = () => {
    trackFunnelEvent('skool_application_submitted', { form_id: 'skool_ads', page_path: window.location.pathname });
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'Free Skool Group Application',
        content_category: 'qualified_software_engineer',
        content_type: 'community_application',
        currency: 'USD',
        value: 0,
      });
    }
  };

  const sendUnqualifiedEvent = (reason: string) => {
    if (window.fbq) {
      window.fbq('trackCustom', 'SkoolApplicationNeedsFoundation', {
        content_name: 'Free Skool Group Application',
        content_category: reason,
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!employmentStatus || !experienceLevel) {
      setError('Please answer the qualification questions before continuing.');
      return;
    }

    setError('');

    const qualificationReason = getQualificationReason(employmentStatus, experienceLevel);

    if (qualificationReason === 'qualified') {
      sendQualifiedEvent();
      setOutcome('qualified');

      window.setTimeout(() => {
        window.open(SKOOL_URL, '_self', 'noopener,noreferrer');
      }, 350);
      return;
    }

    sendUnqualifiedEvent(qualificationReason);
    setOutcome('needs_foundation');

    window.setTimeout(() => {
      document.getElementById('video-track')?.scrollIntoView?.({ behavior: 'smooth' });
    }, 50);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        overflow: 'hidden',
        background:
          'radial-gradient(circle at top left, rgba(235, 203, 139, 0.22), transparent 30%), linear-gradient(135deg, #101820 0%, #17242f 48%, #25313d 100%)',
        color: 'text.primary',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 }, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 32,
            right: { xs: -120, md: -40 },
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'rgba(136, 192, 208, 0.13)',
            filter: 'blur(4px)',
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 7 }}>
          <Typography
            component="a"
            href="/"
            sx={{
              color: '#EBCB8B',
              fontWeight: 900,
              fontSize: '1.25rem',
              letterSpacing: 1,
              textDecoration: 'none',
            }}
          >
            RAY JONES
          </Typography>
          <Typography sx={{ color: '#A3BE8C', fontWeight: 800, fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
            Free Skool Community
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            mb: { xs: 5, md: 7 },
            borderRadius: 5,
            border: '1px solid rgba(235, 203, 139, 0.35)',
            bgcolor: 'rgba(16, 24, 32, 0.78)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.35)',
          }}
        >
          <Box sx={{ textAlign: 'center', px: { xs: 1, md: 2 }, pt: { xs: 1, md: 2 }, pb: 2 }}>
            <Typography variant="overline" sx={{ color: '#EBCB8B', fontWeight: 950, letterSpacing: 1.8 }}>
              Watch This First
            </Typography>
            <Typography variant="h4" sx={{ color: '#F8FAFC', fontWeight: 950, mt: 0.5 }}>
              See how the free community helps working engineers get unstuck
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', pt: '56.25%', borderRadius: 4, overflow: 'hidden' }}>
            <iframe
              src={LOOM_URL}
              title="Free Skool Community VSL"
              frameBorder="0"
              allowFullScreen
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.03fr 0.97fr' },
            gap: { xs: 5, md: 7 },
            alignItems: 'center',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                mb: 3,
                borderRadius: 99,
                bgcolor: 'rgba(163, 190, 140, 0.14)',
                border: '1px solid rgba(163, 190, 140, 0.35)',
                color: '#D8DEE9',
                fontWeight: 800,
              }}
            >
              <CheckCircle sx={{ fontSize: 18, color: '#A3BE8C' }} />
              For employed software engineers with 1+ year of experience
            </Box>

            <Typography
              variant="h1"
              sx={{
                color: '#F8FAFC',
                fontWeight: 950,
                fontSize: { xs: '2.6rem', md: '4.7rem' },
                lineHeight: 0.95,
                letterSpacing: '-0.06em',
                mb: 3,
              }}
            >
              Get unstuck on LeetCode without doing it alone.
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#D8DEE9',
                lineHeight: 1.55,
                maxWidth: 680,
                mb: 4,
              }}
            >
              Join my completely free Skool group for working software engineers who want more interviews,
              cleaner resumes, and a repeatable plan for coding interviews.
            </Typography>

            <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
              {communityBenefits.map((benefit) => (
                <Box key={benefit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#A3BE8C', mt: 0.2 }} />
                  <Typography sx={{ color: '#ECEFF4', fontSize: '1.04rem' }}>{benefit}</Typography>
                </Box>
              ))}
            </Box>

            <Typography sx={{ color: '#EBCB8B', fontWeight: 900 }}>
              Answer two quick questions below and I will send you to the best next step.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 5,
              border: '1px solid rgba(236, 239, 244, 0.16)',
              bgcolor: 'rgba(16, 24, 32, 0.78)',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.35)',
              backdropFilter: 'blur(14px)',
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: '#88C0D0', fontWeight: 900, letterSpacing: 1.8 }}
            >
              Quick Application
            </Typography>
            <Typography variant="h4" sx={{ color: '#F8FAFC', fontWeight: 900, mb: 1 }}>
              See if the free group is right for you
            </Typography>
            <Typography sx={{ color: '#D8DEE9', mb: 3 }}>
              Qualified applicants are sent straight to Skool. Everyone else gets the foundation videos first.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <FormControl sx={{ width: '100%' }}>
                <Typography sx={{ color: '#F8FAFC', fontWeight: 800, mb: 1 }}>
                  Are you currently employed as a software engineer?
                </Typography>
                <RadioGroup
                  value={employmentStatus}
                  onChange={(event) => setEmploymentStatus(event.target.value as EmploymentStatus)}
                >
                  <FormControlLabel
                    value="employed_swe"
                    control={<Radio />}
                    label="Yes, I am currently employed as a software engineer"
                  />
                  <FormControlLabel
                    value="employed_technical"
                    control={<Radio />}
                    label="I work in tech, but not as a software engineer"
                  />
                  <FormControlLabel
                    value="previously_swe_unemployed"
                    control={<Radio />}
                    label="I've worked as a software engineer, but now unemployed"
                  />
                  <FormControlLabel value="student" control={<Radio />} label="I am a student or bootcamp learner" />
                  <FormControlLabel
                    value="not_employed"
                    control={<Radio />}
                    label="I am not currently employed"
                  />
                  <FormControlLabel
                    value="career_switcher"
                    control={<Radio />}
                    label="I am switching careers into software engineering"
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                select
                label="How much software engineering experience do you have?"
                value={experienceLevel}
                onChange={(event) => setExperienceLevel(event.target.value as ExperienceLevel)}
                fullWidth
                required
                sx={{ mt: 2 }}
              >
                <MenuItem value="under_1">Less than 1 year</MenuItem>
                <MenuItem value="1_2">1-2 years</MenuItem>
                <MenuItem value="3_5">3-5 years</MenuItem>
                <MenuItem value="6_plus">6+ years</MenuItem>
              </TextField>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {outcome === 'qualified' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  You qualify. Sending you to the free Skool group now.
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                endIcon={<ArrowForward />}
                sx={{
                  mt: 3,
                  py: 1.7,
                  color: '#101820',
                  bgcolor: '#EBCB8B',
                  fontWeight: 950,
                  fontSize: '1.08rem',
                  '&:hover': {
                    bgcolor: '#F0D79C',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Apply for the Free Group
              </Button>

              <Typography sx={{ color: '#B8C2CC', fontSize: '0.9rem', mt: 2, textAlign: 'center' }}>
                Meta only receives CompleteRegistration when you qualify.
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
            mt: 8,
          }}
        >
          {qualificationSignals.map((signal) => (
            <Card
              key={signal.title}
              sx={{
                bgcolor: 'rgba(236, 239, 244, 0.07)',
                border: '1px solid rgba(236, 239, 244, 0.12)',
                color: '#ECEFF4',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ color: '#88C0D0', mb: 1 }}>{signal.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                  {signal.title}
                </Typography>
                <Typography sx={{ color: '#D8DEE9' }}>{signal.copy}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {outcome === 'needs_foundation' && (
          <Box id="video-track" sx={{ mt: 9 }}>
            <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 4 }}>
              <School sx={{ color: '#EBCB8B', fontSize: 46, mb: 1 }} />
              <Typography variant="h3" sx={{ color: '#F8FAFC', fontWeight: 950, mb: 2 }}>
                Not qualified yet? Start here first.
              </Typography>
              <Typography sx={{ color: '#D8DEE9', fontSize: '1.12rem', lineHeight: 1.7 }}>
                I made a 1.5 hour guide to getting your first SWE job. Check this out.
              </Typography>
            </Box>

            <Alert severity="info" sx={{ maxWidth: 760, mx: 'auto', mb: 4 }}>
              I'd recommend you watch this one hour guide.
            </Alert>

            <Card
              sx={{
                maxWidth: 920,
                mx: 'auto',
                bgcolor: 'rgba(16, 24, 32, 0.72)',
                border: '1px solid rgba(136, 192, 208, 0.32)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'relative', pt: '56.25%' }}>
                <iframe
                  src={FOUNDATION_LOOM_URL}
                  title="First SWE Job Guide"
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
              <CardContent
                sx={{
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h5" sx={{ color: '#F8FAFC', fontWeight: 900, mb: 1 }}>
                  One-hour guide for landing your first SWE job
                </Typography>
                <Typography sx={{ color: '#D8DEE9' }}>
                  Start here, then come back when you are actively working as a software engineer.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SkoolAdsLandingPage;
